import ProductCouponDisplay from '@c/ProductCouponDisplay';
import type { PublicUserDocument } from '@models/user';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@ui/Button';
import {
  addProductToFavorites,
  getInvalidVariationsText,
  hasValidVariations,
  removeProductFromFavorites,
} from '@util/firestore/products';
import { trackUserInteraction } from '@util/firestore/recommendations';
import { useOnlineStatus } from '@util/hooks/useOnlineStatus';
import { getCartItemFromProductDocument } from '@util/index';
import { useAttributionContext } from 'context/AttributionContext';
import { useAuth } from 'context/AuthContext';
import { useShoppingCart } from 'context/ShoppingCartContext';
import { useToastContext } from 'context/ToastContext';
import dayjs from 'dayjs';
import MakeAnOfferButton from 'features/makeAnOffer/components/MakeAnOfferButton';
import { HeartIcon } from 'lucide-react';
import type { ProductDocument, Variation } from 'models/product';
import { useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { MinusIcon, PlusIcon } from '@heroicons/react/20/solid';
import { CheckCircle } from '@c/icons/CheckCircle';
import { getPublicUserDoc } from '@util/firestore/users';

type BuyAddOfferButtonsProps = {
  product: ProductDocument;
  seller?: PublicUserDocument | null;
  selectedVariations: Variation[] | null;
  onAddToCartError: () => void;
  hideOffer?: boolean;
  customInputs?: {
    enabled: boolean;
    values: Record<string, string>;
    cost: number;
  };
  dismissQuickView?: () => void;
  pathname?: string | null;
  isOffer?: boolean;
};

export function hasValidCustomInput(
  product: ProductDocument,
  customInputs: {
    enabled: boolean;
    values: Record<string, string>;
    cost: number;
  }
) {
  if (!product.custom_inputs || !customInputs.enabled) return true;

  // Check that all required fields have values
  return product.custom_inputs.fields.every((field) => {
    if (!field.required) return true;
    return !!customInputs.values[field.id];
  });
}

function getQtyOfVariationInStock(
  product: ProductDocument,
  selectedVariations: Variation[]
) {
  if (product.category2 === 'Gear Combos') {
    const qty1 = product.variations?.find(
      (v) =>
        (v.is_number && v.size === selectedVariations?.[0]?.size) ||
        v.size === selectedVariations?.[1]?.size
    )?.qty;
    const qty2 = product.variations?.find(
      (v) =>
        (!v.is_number && v.size === selectedVariations?.[0]?.size) ||
        v.size === selectedVariations?.[1]?.size
    )?.qty;
    return qty1 && qty2 ? Math.min(qty1, qty2) : qty1 || qty2;
  }
  if (!selectedVariations || selectedVariations.length === 0) return 0;
  let qty = 0;
  const letter = selectedVariations.find((v) => !v.is_number && v.size);
  const number = selectedVariations.find((v) => v.is_number && v.size);
  const color = selectedVariations.find((v) => v.color);
  product.variations?.forEach((v) => {
    if (
      (v.size === letter?.size || !letter) &&
      (v.size === number?.size || !number) &&
      (v.color === color?.color || !color)
    ) {
      qty += v.qty;
    }
  });
  return qty;
}

export const BuyAddOfferButtons = ({
  product,
  selectedVariations,
  onAddToCartError,
  hideOffer,
  customInputs,
  dismissQuickView,
  pathname,
  seller: initialSeller,
  isOffer,
}: BuyAddOfferButtonsProps) => {
  const { userDoc, user, signInAnon } = useAuth();
  const { increaseCartQty, canAddProductToCart, cart, setCartOpen } =
    useShoppingCart();
  const [liked, setLiked] = React.useState(false);
  const { getAttribution } = useAttributionContext();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { showErrorToast } = useToastContext();
  const [nextAction, setNextAction] = useState<'' | 'buyNow' | 'addToCart'>('');
  const [quantity, setQuantity] = useState<number>(1);

  const productQty =
    (product.has_variations
      ? getQtyOfVariationInStock(product, selectedVariations ?? [])
      : product.stock) ?? 0;

  React.useEffect(() => {
    if (userDoc) {
      const isFavorite = userDoc.favorites.includes(product.id);
      setLiked(isFavorite);
    }
  }, [userDoc, product.id]);

  const [loadingAddToCart, setLoadingAddToCart] = useState(false);

  const handleIncreaseQty = () => {
    setQuantity((prev) => {
      const max = productQty ?? 1;
      return prev < max ? prev + 1 : max;
    });
  };

  const handleDecreaseQty = () => {
    setQuantity((prev) => (prev > 1 ? prev - 1 : 1));
  };

  const hasAddToCartError = () => {
    try {
      if (!hasValidVariations(product, selectedVariations)) {
        showErrorToast(
          getInvalidVariationsText(product, selectedVariations) ||
            'Invalid product variations'
        );
        onAddToCartError();
        return true;
      }

      if (
        customInputs?.enabled &&
        !hasValidCustomInput(product, customInputs)
      ) {
        showErrorToast('Please fill out all required fields');
        return true;
      }

      if (
        !canAddProductToCart(product.id, selectedVariations) &&
        showBuyButton
      ) {
        showErrorToast('This product cannot be added to cart at this time');
        return true;
      }

      return false;
    } catch (error) {
      showErrorToast('An unexpected error occurred while validating cart');
      console.error('Cart validation error:', error);
      return true;
    }
  };

  const handleAddToCart = async () => {
    try {
      if (user) {
        if (!hasValidVariations(product, selectedVariations)) {
          showErrorToast(
            getInvalidVariationsText(product, selectedVariations) ||
              'Invalid product variations'
          );
          onAddToCartError();
          return;
        }

        if (product.out_of_stock) {
          showErrorToast('Product out of stock');
          return;
        }

        if (
          customInputs?.enabled &&
          !hasValidCustomInput(product, customInputs)
        ) {
          showErrorToast('Please fill out all required fields');
          return;
        }

        const canAddToCart = canAddProductToCart(
          product.id,
          selectedVariations
        );

        if (!canAddToCart && showBuyButton) {
          showErrorToast('This product cannot be added to cart at this time');
          return;
        }
      } else {
        try {
          setLoadingAddToCart(true);
          await signInAnon();
          setNextAction('addToCart');
        } catch (error) {
          setLoadingAddToCart(false);
          showErrorToast(`Authentication failed: ${(error as Error).message}`);
        } finally {
          return;
        }
      }

      const attribution = getAttribution(product.id);

      const cartItem = getCartItemFromProductDocument(
        product,
        selectedVariations,
        attribution,
        customInputs,
        quantity
      );

      if (hasAddToCartError()) return;

      increaseCartQty(cartItem, selectedVariations);
      dismissQuickView?.();

      if (pathname !== '/my-cart') {
        setCartOpen(true);
      }
    } catch (error) {
      showErrorToast(`Failed to add to cart: ${(error as Error).message}`);
      console.error('Add to cart error:', error);
      setLoadingAddToCart(false);
    }
  };

  const [loadingBuyNow, setLoadingBuyNow] = useState(false);

  const handleBuyNow = async () => {
    if (user) {
      if (
        hasValidVariations(product, selectedVariations) &&
        canAddProductToCart(product.id, selectedVariations) &&
        showBuyButton
      ) {
        if (
          customInputs?.enabled &&
          !hasValidCustomInput(product, customInputs)
        ) {
          showErrorToast('Please fill out all required fields');
          return;
        }
        const cartItem = getCartItemFromProductDocument(
          product,
          selectedVariations,
          undefined,
          customInputs,
          quantity
        );
        increaseCartQty(cartItem);
        router.push('/checkout');
      } else {
        onAddToCartError();
      }
    } else {
      try {
        setLoadingBuyNow(true);
        await signInAnon();
        setNextAction('buyNow');
      } catch (e) {
        setLoadingBuyNow(false);
        showErrorToast((e as Error).message);
      } finally {
        return;
      }
    }
    if (!hasValidVariations(product, selectedVariations)) {
      showErrorToast(
        getInvalidVariationsText(product, selectedVariations) ||
          'Unable to add to cart'
      );
      onAddToCartError();
      return;
    }
    if (!canAddProductToCart(product.id, selectedVariations) && showBuyButton) {
      showErrorToast('Cannot add product to cart');
      onAddToCartError();
      setCartOpen(true);
      return;
    }
    if (customInputs?.enabled && !hasValidCustomInput(product, customInputs)) {
      showErrorToast('Please fill out all required fields');
      onAddToCartError();
      return;
    }
    const cartItem = getCartItemFromProductDocument(
      product,
      selectedVariations,
      undefined,
      customInputs,
      quantity
    );
    increaseCartQty(cartItem, selectedVariations);
    router.push('/checkout');
    setQuantity(1);
    dismissQuickView?.();
  };

  const handleLikeClick = async () => {
    if (!userDoc?.uid) {
      router.push('/login');
      return;
    }
    const isFavorite = userDoc.favorites.includes(product.id);
    if (isFavorite) {
      await removeProductFromFavorites(userDoc.uid, product.id);
    } else {
      await addProductToFavorites(userDoc.uid, product.id);
    }
    await trackUserInteraction({
      uid: userDoc.uid,
      pid: product.id,
      interaction: isFavorite ? 'unfavorite' : 'favorite',
    });

    setLiked(!liked);

    queryClient.invalidateQueries({
      queryKey: ['authUser'],
    });
  };

  const { data: seller = initialSeller } = useQuery({
    queryKey: ['publicUser', product.seller_id],
    queryFn: () => getPublicUserDoc({ uid: product.seller_id }),
  });

  const showBuyButton = !seller?.is_inactive && !seller?.on_vacation;

  useEffect(() => {
    if (nextAction && user && userDoc && cart) {
      if (nextAction === 'buyNow') {
        handleBuyNow();
      } else if (nextAction === 'addToCart') {
        handleAddToCart();
        setLoadingAddToCart(false);
      }
      setNextAction('');
    }
  }, [nextAction, user, userDoc, cart]);
  return (
    <div className="flex flex-col gap-[1rem]">
      {pathname !== '/my-cart' && productQty > 0 && (
        <div className="mb-4 flex flex-col items-start gap-[1.5rem]">
          <span className="text-[1.8rem] font-medium text-brand-black">
            Quantity
          </span>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-x-[1.5rem] rounded-[1rem] bg-[#F1F1F1] px-[1.5rem] py-[0.8rem]">
              <button
                type="button"
                className="flex items-center justify-center rounded-full border border-zinc-100 bg-white p-1 text-gray-600 transition-colors hover:text-black disabled:opacity-40"
                onClick={handleDecreaseQty}
                disabled={quantity <= 1}
              >
                <MinusIcon className="h-[1.8rem] w-[1.8rem]" />
              </button>
              <span className="min-w-[3rem] text-center text-[1.6rem] font-medium text-black">
                {quantity}
              </span>
              <button
                type="button"
                className="flex items-center justify-center rounded-full bg-black p-2 text-white transition-colors disabled:opacity-40"
                onClick={handleIncreaseQty}
                disabled={quantity >= productQty}
              >
                <PlusIcon className="h-[1.8rem] w-[1.8rem]" />
              </button>
            </div>

            <div className="flex items-center gap-4">
              {!!selectedVariations?.length && productQty === 1 ? (
                <span className="text-[1.8rem] font-medium text-brand-secondary">
                  Only one left!
                </span>
              ) : (
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-9 w-9" fill="#1E7A1E" />

                  <span className="text-[1.8rem] font-medium text-zinc-700">
                    {productQty > 20 ? '20+' : productQty} in stock
                  </span>
                </div>
              )}

              {product.sold_count && product.sold_count > 0 && (
                <>
                  <div className="mt-1 h-2 w-2 rounded-full bg-zinc-400" />

                  <span className="text-[1.8rem] font-medium text-brand-secondary">
                    {product.sold_count} sold
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showBuyButton && pathname !== '/my-cart' && (
        <Button
          type="secondary"
          text="Buy It Now"
          width="fluid"
          loading={loadingBuyNow}
          disabled={product.out_of_stock}
          onClick={handleBuyNow}
          className="!text-[1.8rem]"
        />
      )}

      {showBuyButton && (
        <Button
          text="Add to Cart"
          width="fluid"
          type="outline"
          loading={loadingAddToCart}
          disabled={product.out_of_stock}
          onClick={handleAddToCart}
          className="!text-[1.8rem]"
        />
      )}

      {pathname !== '/my-cart' && (
        <>
          {hideOffer ? (
            <div className="hidden sm:flex">
              <Button
                type="outline"
                width="fluid"
                text={liked ? 'In Favorites' : 'Add to Favorites'}
                leadingIcon={
                  <HeartIcon
                    className={`${
                      liked ? 'fill-white' : '!text-brand-secondary'
                    } h-10 w-10`}
                  />
                }
                className={`${
                  liked
                    ? '!border-none !bg-brand-secondary text-white'
                    : 'border-brand-secondary text-brand-secondary'
                }  text-[1.8rem]`}
                disabled={product.out_of_stock || productQty === 0}
                onClick={handleLikeClick}
              />
            </div>
          ) : (
            <>
              {!isOffer ? (
                <MakeAnOfferButton
                  product={product}
                  selectedVariations={selectedVariations}
                />
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default BuyAddOfferButtons;
